import { ADS_ENDPOINTS } from "./endpoints";
import type {
  AdCampaign,
  AdCampaignFormData,
  AdCampaignListParams,
  Advertiser,
  AdvertiserListParams,
  CreateAdvertiserPayload,
  CreateAdvertiserResult,
  AdsAuditLog,
  AdsAuditLogListParams,
  AdsCampaignStatus,
  AdsAdvertiserStatus,
  AdsPricingModel,
  AdsOverview,
  AdsBudgetAlertItem,
  AdsExpiringCampaignItem,
  AdsOverviewStat,
  BillingItem,
  BillingListParams,
  Creative,
  CreativeListParams,
  ListResponse,
  Placement,
  PlacementListParams,
  Report,
  ReportListParams,
  ReportAnalytics,
  ReportAnalyticsParams,
  AdsPaginationParams,
  CreateAdPayload,
  CreateAdResult,
  UpdateAdPayload,
  AdPerformanceAnalytics,
  AdPerformanceParams,
  AdCampaignPricingModelUI,
} from "./types";
import { mockReports } from "./mockData";
import secureApi from "../../utils/secureApi";
import { getImageUrl } from "../../utils/imageUrl";

export { ADS_ENDPOINTS };
export * from "./types";

const defaultPageNum = 1;
const defaultPageSize = 10;

function toPageParams(params?: AdsPaginationParams) {
  return {
    pageNum: params?.pageNum ?? defaultPageNum,
    pageSize: params?.pageSize ?? defaultPageSize,
  };
}

function paginate<T>(items: T[], pageNum: number, pageSize: number): ListResponse<T> {
  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePageNum = Math.min(Math.max(1, pageNum), totalPages);
  const start = (safePageNum - 1) * pageSize;
  const end = start + pageSize;
  return {
    results: items.slice(start, end),
    totalCount,
    currentPage: safePageNum,
    totalPages,
  };
}

function safeLower(val: unknown) {
  return typeof val === "string" ? val.toLowerCase() : "";
}

export async function getAdvertisers(
  params: AdvertiserListParams = {}
): Promise<ListResponse<Advertiser>> {
  const queryParams = new URLSearchParams();
  queryParams.append("page", (params.pageNum ?? 1).toString());
  queryParams.append("limit", (params.pageSize ?? 10).toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.status && params.status !== "ALL") queryParams.append("status", params.status);

  const endpoint = `${ADS_ENDPOINTS.ADVERTISERS.LIST}?${queryParams.toString()}`;
  const response = await secureApi.get(endpoint);

  const data = response?.data || response;
  const pagination = data?.pagination;
  const rawList: any[] =
    data?.advertisers ??
    data?.paginatedResults ??
    data?.results ??
    (Array.isArray(data) ? data : []);

  const results: Advertiser[] = rawList.map((item: any): Advertiser => ({
    id: item._id ?? item.id ?? "",
    name: item.name ?? "",
    contactEmail: item.contact_email ?? "",
    status: item.status as AdsAdvertiserStatus,
    createdAt: item.created_at ?? item.createdAt ?? "",
  }));

  const pageSize = params.pageSize ?? 10;
  const totalCount: number = pagination?.total ?? data?.totalCount ?? results.length;
  const currentPage: number = pagination?.page ?? params.pageNum ?? 1;
  const totalPages: number = pagination?.pages ?? data?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

  return { results, totalCount, currentPage, totalPages };
}

export async function createAdvertiser(payload: CreateAdvertiserPayload): Promise<CreateAdvertiserResult> {
  try {
    const body = { name: payload.name, contact_email: payload.contactEmail };
    const response = await secureApi.post(ADS_ENDPOINTS.ADVERTISERS.CREATE, body);
    const data = response?.data ?? response;
    const advertiser: Advertiser = {
      id: data._id ?? data.id ?? "",
      name: data.name ?? payload.name,
      contactEmail: data.contact_email ?? payload.contactEmail,
      status: data.status as AdsAdvertiserStatus,
      createdAt: data.created_at ?? data.createdAt ?? "",
    };
    return { advertiser, message: response?.message ?? "Advertiser created successfully" };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to create advertiser. Please try again."
    );
  }
}

export async function updateAdvertiserStatus(
  id: string,
  status: AdsAdvertiserStatus
): Promise<{ advertiser: Advertiser; message: string }> {
  try {
    const endpoint = ADS_ENDPOINTS.ADVERTISERS.UPDATE_STATUS.replace("{id}", id);
    const response = await secureApi.put(endpoint, { status });
    const data = response?.data ?? response;
    const advertiser: Advertiser = {
      id: data._id ?? data.id ?? id,
      name: data.name ?? "",
      contactEmail: data.contact_email ?? "",
      status: data.status as AdsAdvertiserStatus,
      createdAt: data.created_at ?? data.createdAt ?? "",
    };
    return { advertiser, message: response?.message ?? "Advertiser status updated successfully" };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to update advertiser status."
    );
  }
}

function normalizePricingModel(raw: string): AdsPricingModel {
  const upper = (raw ?? "").toUpperCase();
  if (upper === "FLAT") return "Flat";
  if (upper === "CPC") return "CPC";
  if (upper === "CPM") return "CPM";
  if (upper === "CPA") return "CPA";
  return "Flat";
}

function normalizeFormPricingModel(raw: string): AdCampaignPricingModelUI {
  const upper = (raw ?? "").toUpperCase();
  if (upper === "CPC") return "CPC";
  if (upper === "CPM") return "CPM";
  return "FLAT";
}

export async function getAdCampaigns(
  params: AdCampaignListParams = {}
): Promise<ListResponse<AdCampaign>> {
  const queryParams = new URLSearchParams();
  queryParams.append("page", (params.pageNum ?? 1).toString());
  queryParams.append("limit", (params.pageSize ?? 10).toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.status && params.status !== "ALL") queryParams.append("status", params.status);
  if (params.pricingModel && params.pricingModel !== "ALL")
    queryParams.append("pricing_model", params.pricingModel === "Flat" ? "FLAT" : params.pricingModel);
  if (params.placement && params.placement !== "ALL") queryParams.append("placement_type", params.placement);
  if (params.advertiserId) queryParams.append("advertiser_id", params.advertiserId);
  if (params.startAt) queryParams.append("start_date", params.startAt);
  if (params.endAt) queryParams.append("end_date", params.endAt);

  const endpoint = `${ADS_ENDPOINTS.CAMPAIGNS.LIST}?${queryParams.toString()}`;
  const response = await secureApi.get(endpoint);
  const data = response?.data ?? response;
  const pagination = data?.pagination;
  const rawList: any[] =
    data?.campaigns ??
    data?.paginatedResults ??
    data?.results ??
    (Array.isArray(data) ? data : []);

  const results: AdCampaign[] = rawList.map((item: any): AdCampaign => {
    const advertiser = item.advertiser_id;
    return {
      id: item._id ?? item.id ?? "",
      name: item.name ?? "",
      advertiserId: advertiser && typeof advertiser === "object"
        ? (advertiser._id ?? advertiser.id ?? "")
        : (typeof advertiser === "string" ? advertiser : ""),
      advertiserName: advertiser && typeof advertiser === "object" ? (advertiser.name ?? "-") : "-",
      pricingModel: normalizePricingModel(item.pricing_model),
      budget: item.total_budget ?? item.pricing?.flat_monthly ?? 0,
      spend: 0,
      placement: item.placement_type ?? "-",
      startAt: item.start_date ?? item.startAt ?? "",
      endAt: item.end_date ?? item.endAt ?? "",
      status: item.status as AdsCampaignStatus,
    };
  });

  const pageSize = params.pageSize ?? 10;
  const totalCount: number = pagination?.total ?? data?.totalCount ?? results.length;
  const currentPage: number = pagination?.page ?? params.pageNum ?? 1;
  const totalPages: number =
    pagination?.pages ?? data?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

  return { results, totalCount, currentPage, totalPages };
}

function formatPlacement(raw: string): string {
  if (!raw) return "";
  return raw
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export async function getCreatives(
  params: CreativeListParams = {}
): Promise<ListResponse<Creative>> {
  const queryParams = new URLSearchParams();
  queryParams.append("page", (params.pageNum ?? 1).toString());
  queryParams.append("limit", (params.pageSize ?? 10).toString());
  if (params.search) queryParams.append("search", params.search);

  const endpoint = `${ADS_ENDPOINTS.CREATIVES.LIST}?${queryParams.toString()}`;
  const response = await secureApi.get(endpoint);
  const data = response?.data ?? response;
  const pagination = data?.pagination;
  const rawList: any[] =
    data?.ads ??
    data?.paginatedResults ??
    data?.results ??
    (Array.isArray(data) ? data : []);

  const results: Creative[] = rawList.map((item: any): Creative => ({
    id: item._id ?? item.id ?? "",
    campaignName: item.campaign_id?.name ?? "",
    placement: formatPlacement(item.placement_type ?? item.placement_id?.type ?? ""),
    status: item.status as AdsCampaignStatus,
    createdAt: item.created_at ?? item.createdAt ?? "",
    previewUrl: getImageUrl(item.creative?.media_url ?? ""),
  }));

  const pageSize = params.pageSize ?? 10;
  const totalCount: number = pagination?.total ?? data?.totalCount ?? results.length;
  const currentPage: number = pagination?.page ?? params.pageNum ?? 1;
  const totalPages: number =
    pagination?.pages ?? data?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

  return { results, totalCount, currentPage, totalPages };
}

export async function updateCreativeStatus(
  id: string,
  status: AdsCampaignStatus
): Promise<{ id: string; status: AdsCampaignStatus; message: string }> {
  try {
    const endpoint = ADS_ENDPOINTS.CREATIVES.UPDATE_STATUS.replace("{id}", id);
    const response = await secureApi.put(endpoint, { status });
    const data = response?.data ?? response;
    return {
      id: data._id ?? data.id ?? id,
      status: (data.status as AdsCampaignStatus) ?? status,
      message: response?.message ?? "Ad status updated successfully",
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to update creative status."
    );
  }
}

export async function getPlacements(
  params: PlacementListParams = {}
): Promise<ListResponse<Placement>> {
  const response = await secureApi.get(ADS_ENDPOINTS.PLACEMENTS.LIST);
  const data = response?.data ?? response;
  const rawList: any[] = Array.isArray(data)
    ? data
    : (data?.results ?? data?.paginatedResults ?? []);

  const search = safeLower(params.search).trim();

  const mapped: Placement[] = rawList.map((item: any): Placement => ({
    id: item._id ?? item.id ?? "",
    name: item.type ?? "",
    description: "",
    enabled: true,
    frequencyCap: item.frequency_cap?.per_user_per_day ?? 0,
    frequencyCapPerSession: item.frequency_cap?.per_session ?? false,
    maxAdvertisers: item.max_ads ?? 0,
  }));

  const results = search
    ? mapped.filter((p) => safeLower(p.name).includes(search))
    : mapped;

  return { results, totalCount: results.length, currentPage: 1, totalPages: 1 };
}

export async function updatePlacement(
  id: string,
  config: { frequencyCap: number | null; frequencyCapPerSession: boolean; maxAdvertisers: number }
): Promise<Placement> {
  try {
    const endpoint = ADS_ENDPOINTS.PLACEMENTS.UPDATE.replace("{id}", id);
    const payload: Record<string, any> = {
      max_ads: config.maxAdvertisers,
      frequency_cap: {
        per_session: config.frequencyCapPerSession,
        ...(config.frequencyCapPerSession ? {} : { per_user_per_day: config.frequencyCap }),
      },
    };
    const response = await secureApi.put(endpoint, payload);
    const data = response?.data ?? response;
    return {
      id: data._id ?? data.id ?? id,
      name: data.type ?? "",
      description: "",
      enabled: true,
      frequencyCap: data.frequency_cap?.per_user_per_day ?? 0,
      frequencyCapPerSession: data.frequency_cap?.per_session ?? false,
      maxAdvertisers: data.max_ads ?? 0,
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to update placement."
    );
  }
}

export async function getReports(
  params: ReportListParams = {}
): Promise<ListResponse<Report>> {
  const { pageNum, pageSize } = toPageParams(params);
  const search = safeLower(params.search).trim();
  const reportType = params.reportType ?? "ALL";

  const filtered = mockReports.filter((r) => {
    const matchesSearch = !search || safeLower(r.title).includes(search);
    const matchesType = reportType === "ALL" || r.reportType === reportType;
    return matchesSearch && matchesType;
  });

  return paginate(filtered, pageNum, pageSize);
}

export async function getBilling(
  params: BillingListParams = {}
): Promise<ListResponse<BillingItem>> {
  const queryParams = new URLSearchParams();
  queryParams.append("page", (params.pageNum ?? 1).toString());
  queryParams.append("limit", (params.pageSize ?? 10).toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.advertiserId) queryParams.append("advertiser_id", params.advertiserId);

  const endpoint = `${ADS_ENDPOINTS.BILLING.LIST}?${queryParams.toString()}`;
  const response = await secureApi.get(endpoint);
  const data = response?.data ?? response;
  const rawList: any[] = data?.entries ?? data?.results ?? [];
  const pagination = data?.pagination;
  const pageSize = params.pageSize ?? 10;
  const totalCount: number = pagination?.total ?? data?.totalCount ?? rawList.length;
  const currentPage: number = pagination?.page ?? params.pageNum ?? 1;
  const totalPages: number = pagination?.pages ?? data?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

  const results: BillingItem[] = rawList.map((item: any): BillingItem => {
    const advertiser = item.advertiser_id;
    const campaign = item.campaign_id;
    return {
      id: item._id ?? item.id ?? "",
      advertiserName:
        advertiser && typeof advertiser === "object"
          ? (advertiser.name ?? "-")
          : (item.advertiser_name ?? "-"),
      campaignName:
        campaign && typeof campaign === "object"
          ? (campaign.name ?? "-")
          : (item.campaign_name ?? "-"),
      eventType: (item.event_type ?? item.eventType ?? "CLICK") as BillingItem["eventType"],
      referenceId: item.reference_id ?? item.referenceId ?? "",
      amount: item.amount ?? 0,
      timestamp: item.created_at ?? item.timestamp ?? "",
    };
  });

  return { results, totalCount, currentPage, totalPages };
}

export async function getReportAnalytics(
  params: ReportAnalyticsParams = {}
): Promise<ReportAnalytics> {
  try {
    const query = new URLSearchParams();
    if (params.dateFrom) query.append("start_date", params.dateFrom);
    if (params.dateTo) query.append("end_date", params.dateTo);
    if (params.advertiserId) query.append("advertiser_id", params.advertiserId);
    if (params.placement && params.placement !== "ALL") query.append("placement_type", params.placement);

    const endpoint = `${ADS_ENDPOINTS.CAMPAIGNS.STATS_ALL}?${query.toString()}`;
    const response = await secureApi.get(endpoint);
    const rows: any[] = response?.data ?? [];

    let totalImpressions = 0;
    let totalClicks = 0;
    let totalAmountSpent = 0;

    const campaignMap: Record<string, {
      campaignId: string;
      campaignName: string;
      impressions: number;
      clicks: number;
      spend: number;
      conversions: number;
    }> = {};

    for (const row of rows) {
      const impressions: number = row.impressions ?? 0;
      const clicks: number = row.clicks ?? 0;
      const spend: number = row.spend ?? 0;
      const conversions: number = row.conversions ?? 0;

      totalImpressions += impressions;
      totalClicks += clicks;
      totalAmountSpent += spend;

      const campaignId: string = row.campaign_id ?? "";
      const campaignName: string = row.campaign?.name ?? campaignId;

      if (!campaignMap[campaignId]) {
        campaignMap[campaignId] = { campaignId, campaignName, impressions: 0, clicks: 0, spend: 0, conversions: 0 };
      }
      campaignMap[campaignId].impressions += impressions;
      campaignMap[campaignId].clicks += clicks;
      campaignMap[campaignId].spend += spend;
      campaignMap[campaignId].conversions += conversions;
    }

    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

    const campaignBreakdown = Object.values(campaignMap).map((c) => ({
      ...c,
      ctr: c.impressions > 0 ? (c.clicks / c.impressions) * 100 : 0,
    }));

    return { totalImpressions, totalClicks, avgCtr, totalAmountSpent, campaignBreakdown };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to load report analytics.";
    throw new Error(errorMessage);
  }
}

export async function getAdsAuditLogs(
  params: AdsAuditLogListParams = {}
): Promise<ListResponse<AdsAuditLog>> {
  const queryParams = new URLSearchParams();
  queryParams.append("page", (params.pageNum ?? 1).toString());
  queryParams.append("limit", (params.pageSize ?? 10).toString());
  if (params.search) queryParams.append("search", params.search);

  const endpoint = `${ADS_ENDPOINTS.AUDIT_LOGS.LIST}?${queryParams.toString()}`;
  const response = await secureApi.get(endpoint);
  const data = response?.data ?? response;
  const pagination = data?.pagination;
  const rawList: any[] = data?.logs ?? data?.results ?? (Array.isArray(data) ? data : []);

  const results: AdsAuditLog[] = rawList.map((item: any): AdsAuditLog => ({
    id: item._id ?? item.id ?? "",
    action: item.action ?? "",
    changedBy: {
      id: item.changed_by?._id ?? item.changed_by?.id ?? "",
      email: item.changed_by?.email ?? "",
      fullName: item.changed_by?.fullName ?? item.changed_by?.full_name ?? "",
    },
    changes: Array.isArray(item.changes) ? item.changes : [],
    entityId: item.entity_id ?? "",
    entityType: item.entity_type ?? "",
    timestamp: item.timestamp ?? item.created_at ?? "",
  }));

  const pageSize = params.pageSize ?? 10;
  const totalCount: number = pagination?.total ?? data?.totalCount ?? results.length;
  const currentPage: number = pagination?.page ?? params.pageNum ?? 1;
  const totalPages: number = pagination?.pages ?? data?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

  return { results, totalCount, currentPage, totalPages };
}

function formatIsoToDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function normalizeStat(raw: any): AdsOverviewStat {
  return {
    value: typeof raw?.value === "number" ? raw.value : 0,
    pctChange: typeof raw?.pct_change === "number" ? raw.pct_change : 0,
  };
}

export async function getAdsOverview(): Promise<AdsOverview> {
  try {
    const response = await secureApi.get(ADS_ENDPOINTS.OVERVIEW.GET);
    const body = response?.data ?? response;

    const rawStats = body?.stats ?? {};

    const stats = {
      activeCampaigns: normalizeStat(rawStats.activeCampaigns),
      totalRevenueMTD: normalizeStat(rawStats.totalRevenueMTD),
      impressionsToday: normalizeStat(rawStats.impressionsToday),
      clicksToday: normalizeStat(rawStats.clicksToday),
      ctrToday: normalizeStat(rawStats.ctrToday),
      activePopups: normalizeStat(rawStats.activePopups),
    };

    const rawAlerts: any[] = Array.isArray(body?.budgetAlerts) ? body.budgetAlerts : [];
    const budgetAlerts: AdsBudgetAlertItem[] = rawAlerts.map((item: any) => {
      const spent = item.spent ?? item.spend ?? 0;
      const budget = item.budget ?? item.total_budget ?? 0;
      const pct =
        item.pct ?? item.percentage ?? item.pct_used ??
        (budget > 0 ? Math.round((spent / budget) * 100) : 0);
      return {
        name: item.name ?? item.campaign_name ?? item.campaign?.name ?? "",
        advertiser: item.advertiser ?? item.advertiser_name ?? item.advertiser?.name ?? "",
        spent,
        budget,
        pct,
      };
    });

    const rawExpiring: any[] = Array.isArray(body?.campaignsExpiringSoon) ? body.campaignsExpiringSoon : [];
    const expiringCampaigns: AdsExpiringCampaignItem[] = rawExpiring.map((item: any) => {
      const daysLeft = item.daysLeft ?? item.days_left ?? 0;
      return {
        name: item.name ?? item.campaign_name ?? item.campaign?.name ?? "",
        advertiser: item.advertiser ?? item.advertiser_name ?? item.advertiser?.name ?? "",
        daysLeft,
        endsDate: formatIsoToDate(item.end_date ?? item.ends_at ?? ""),
        urgent: daysLeft <= 2,
      };
    });

    return { stats, budgetAlerts, expiringCampaigns };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to load overview.";
    throw new Error(errorMessage);
  }
}

export async function exportAdsOverview(): Promise<Blob> {
  try {
    const blob = await secureApi.get<Blob>(ADS_ENDPOINTS.OVERVIEW.EXPORT, {
      responseType: "blob",
      headers: {
        "Accept": "text/csv",
        "Content-Type": "text/csv",
      },
    });
    return blob;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
      error?.message ||
      "Failed to export overview report."
    );
  }
}

export async function exportCampaignStats(params: { dateFrom?: string; dateTo?: string } = {}): Promise<Blob> {
  try {
    const query = new URLSearchParams();
    if (params.dateFrom) query.append("start_date", params.dateFrom);
    if (params.dateTo) query.append("end_date", params.dateTo);
    const endpoint = `${ADS_ENDPOINTS.CAMPAIGNS.STATS_ALL_EXPORT}?${query.toString()}`;
    const blob = await secureApi.get<Blob>(endpoint, {
      responseType: "blob",
      headers: {
        "Accept": "text/csv",
        "Content-Type": "text/csv",
      },
    });
    return blob;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
      error?.message ||
      "Failed to export campaign stats report."
    );
  }
}

function pricingRateKey(model: string): string {
  switch (model.toUpperCase()) {
    case "CPC": return "cpc";
    case "CPM": return "cpm";
    case "CPA": return "cpa";
    default:    return "flat_monthly";
  }
}

export interface CreateAdCampaignResult {
  id: string;
  message: string;
}

export interface GetAdCampaignByIdResult {
  formData: Partial<AdCampaignFormData>;
  status: AdsCampaignStatus;
}

export async function getAdCampaignById(id: string): Promise<GetAdCampaignByIdResult> {
  try {
    const endpoint = ADS_ENDPOINTS.CAMPAIGNS.GET_BY_ID.replace("{id}", id);
    const response = await secureApi.get(endpoint);
    const data = response?.data ?? response;

    const model = normalizeFormPricingModel(data?.pricing_model ?? "");
    const rateKey = pricingRateKey((data?.pricing_model ?? "").toUpperCase());
    const pricingRate: number | "" =
      typeof data?.pricing?.[rateKey] === "number" ? data.pricing[rateKey] : "";

    const startDate = data?.start_date ? (data.start_date as string).slice(0, 10) : "";
    const endDate = data?.end_date ? (data.end_date as string).slice(0, 10) : "";

    const advertiserId =
      data?.advertiser_id && typeof data.advertiser_id === "object"
        ? (data.advertiser_id._id ?? data.advertiser_id.id ?? "")
        : "";
    const advertiserName =
      data?.advertiser_id && typeof data.advertiser_id === "object"
        ? (data.advertiser_id.name ?? "")
        : "";

    return {
      formData: {
        campaignName: data?.name ?? "",
        pricingModel: model,
        pricingRate,
        dailyBudget: typeof data?.daily_budget === "number" ? data.daily_budget : "",
        totalBudget: typeof data?.total_budget === "number" ? data.total_budget : "",
        startDate,
        endDate,
        advertiserId,
        advertiserName,
      },
      status: (data?.status as AdsCampaignStatus) ?? ("DRAFT" as AdsCampaignStatus),
    };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to load campaign.";
    throw new Error(errorMessage);
  }
}

export async function createAd(payload: CreateAdPayload): Promise<CreateAdResult> {
  try {
    const response = await secureApi.post(ADS_ENDPOINTS.CREATIVES.CREATE, payload);
    const data = response?.data ?? response;
    const id: string = data?._id ?? data?.id ?? "";
    const message: string = response?.message ?? "Ad created successfully";
    return { id, message };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to create ad. Please try again.";
    throw new Error(errorMessage);
  }
}

export async function getAdById(id: string): Promise<any> {
  try {
    const endpoint = ADS_ENDPOINTS.CREATIVES.GET_BY_ID.replace("{id}", id);
    const response = await secureApi.get(endpoint);
    return response?.data ?? response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to fetch ad."
    );
  }
}

export async function updateAd(id: string, payload: UpdateAdPayload): Promise<CreateAdResult> {
  try {
    const endpoint = ADS_ENDPOINTS.CREATIVES.UPDATE.replace("{id}", id);
    const response = await secureApi.patch(endpoint, payload);
    const data = response?.data ?? response;
    return {
      id: data?._id ?? data?.id ?? id,
      message: response?.message ?? "Ad updated successfully",
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to update ad."
    );
  }
}

export async function getAdAnalytics(
  params: AdPerformanceParams
): Promise<AdPerformanceAnalytics> {
  try {
    const endpoint = ADS_ENDPOINTS.CREATIVES.ANALYTICS.replace("{id}", params.adId);
    const query = new URLSearchParams({
      start_date: params.startDate,
      end_date: params.endDate,
    });
    const response = await secureApi.get(`${endpoint}?${query.toString()}`);
    const data = response?.data ?? response;
    return {
      summary: {
        impressions: data?.summary?.impressions ?? 0,
        clicks: data?.summary?.clicks ?? 0,
        ctr: data?.summary?.ctr ?? 0,
        conversions: data?.summary?.conversions ?? 0,
        conversion_rate: data?.summary?.conversion_rate ?? 0,
      },
      daily: Array.isArray(data?.daily)
        ? data.daily.map((row: any) => ({
            date: row.date ?? "",
            impressions: row.impressions ?? 0,
            clicks: row.clicks ?? 0,
            ctr: row.ctr ?? 0,
          }))
        : [],
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to load ad performance analytics."
    );
  }
}

export async function getTournamentsForAds(
  search?: string
): Promise<{ id: string; name: string }[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await secureApi.get(`/api/v2/tournaments/multi/list${query}`);
  const data = response?.data ?? response;
  const rawList: any[] = Array.isArray(data)
    ? data
    : (data?.results ?? data?.tournaments ?? data?.data ?? []);
  return rawList.map((item: any) => ({
    id: item._id ?? item.id ?? "",
    name: item.name ?? item.title ?? "",
  }));
}

export async function updateCampaignStatus(
  id: string,
  status: AdsCampaignStatus
): Promise<{ id: string; status: AdsCampaignStatus; message: string }> {
  try {
    const endpoint = ADS_ENDPOINTS.CAMPAIGNS.UPDATE_STATUS.replace("{id}", id);
    const response = await secureApi.put(endpoint, { status });
    const data = response?.data ?? response;
    return {
      id: data._id ?? data.id ?? id,
      status: (data.status as AdsCampaignStatus) ?? status,
      message: response?.message ?? "Campaign status updated successfully",
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
      error?.message ??
      "Failed to update campaign status."
    );
  }
}

export async function createAdCampaign(formData: AdCampaignFormData): Promise<CreateAdCampaignResult> {
  try {
    const model = (formData.pricingModel as string).toUpperCase();
    const rateKey = pricingRateKey(model);

    const payload = {
      advertiser_id: formData.advertiserId,
      name: formData.campaignName,
      pricing_model: model,
      pricing: { [rateKey]: Number(formData.pricingRate) },
      start_date: new Date(`${formData.startDate}T00:00:00.000Z`).toISOString(),
      end_date: new Date(`${formData.endDate}T23:59:59.999Z`).toISOString(),
      daily_budget: Number(formData.dailyBudget),
      total_budget: Number(formData.totalBudget),
    };

    const response = await secureApi.post(ADS_ENDPOINTS.CAMPAIGNS.CREATE, payload);
    const data = response?.data ?? response;
    const id: string = data?._id ?? data?.id ?? "";
    const message: string = response?.message ?? "Campaign created successfully";
    return { id, message };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to create campaign. Please try again.";
    throw new Error(errorMessage);
  }
}

export async function updateAdCampaign(id: string, formData: AdCampaignFormData): Promise<{ id: string; message: string }> {
  try {
    const model = (formData.pricingModel as string).toUpperCase();
    const rate = Number(formData.pricingRate);

    const payload = {
      name: formData.campaignName,
      pricing_model: model,
      pricing: {
        flat_monthly: model === "FLAT" ? rate : 0,
        cpm: model === "CPM" ? rate : 0,
        cpc: model === "CPC" ? rate : 0,
      },
      start_date: new Date(`${formData.startDate}T00:00:00.000Z`).toISOString(),
      end_date: new Date(`${formData.endDate}T23:59:59.999Z`).toISOString(),
      daily_budget: Number(formData.dailyBudget),
      total_budget: Number(formData.totalBudget),
    };

    const endpoint = ADS_ENDPOINTS.CAMPAIGNS.UPDATE.replace("{id}", id);
    const response = await secureApi.put(endpoint, payload);
    const data = response?.data ?? response;
    return {
      id: data?._id ?? data?.id ?? id,
      message: response?.message ?? "Campaign updated successfully",
    };
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to update campaign. Please try again.";
    throw new Error(errorMessage);
  }
}

